import { toast } from '@ooh/ui/components/sonner';
import { parseApiError, type CellEntry } from '@ooh/contracts';
import { apiErrorToUserMessage } from '../../../lib/api-error-messages';
import type {
  MultiCellSelection,
  MultiSelectCellRef,
  EntriesGridData,
} from '../../../availability';
import type { UnifiedSelectCellResolution } from '../../../availability/lib/unified-select-types';
import { narrowDateRange } from '../../../lib/date-range-fold';

// ─── Shared SELECT/INSPECT wiring (routes/media-units/index.tsx +
// routes/media-units/$id/index.tsx, unify-availability-interaction task 4.4) ──────

/**
 * Builds the `unitId|isoDateFrom` key format shared by every conflict-key/skipped-key
 * consumer below, from an already-resolved `unitId` (in whatever id space the calling
 * surface's `MultiSelectCellRef.unitId` uses — media-unit id for the inventory catalog and
 * single-unit detail routes, offer-unit id for the offer-draft tab) and a concrete `dateFrom`
 * Date (no period lookup needed — used directly against a batch-response entry's own
 * `dateFrom`). Single source of truth for the key format so `buildAvailabilityCellConflictKey`
 * (period-lookup based) and every route's own conflicted-key construction (response-shape
 * based) can never format the key differently.
 */
export function buildAvailabilityConflictKeyFromDate(unitId: string, dateFrom: Date): string {
  return `${unitId}|${dateFrom.toISOString()}`;
}

/**
 * Builds the `mediaUnitId|isoDateFrom` key used to match a `MultiSelectCellRef` against a
 * `BatchReserveResponse.conflicted[]` entry. Single source of truth shared by the inventory
 * catalog and single-unit detail routes, which both drop conflicted cells from an active
 * multi-selection after a batch-reserve response.
 */
export function buildAvailabilityCellConflictKey(
  ref: MultiSelectCellRef,
  periods: { id: string; start: string }[],
): string {
  const period = periods.find((p) => p.id === ref.periodId);
  if (!period) return `${ref.unitId}|unknown`;
  return buildAvailabilityConflictKeyFromDate(
    ref.unitId,
    new Date(`${period.start}T00:00:00.000Z`),
  );
}

/**
 * Drops every cell matching a `BatchReserveResponse.conflicted[]`-derived key
 * (built via `buildAvailabilityCellConflictKey`) from an active `MultiCellSelection`,
 * returning `null` once no cells remain. Single source of truth shared by the
 * inventory catalog route, single-unit detail route, and the offer-draft
 * availability tab — all three drop conflicted cells from an active multi-selection
 * after a batch-reserve response.
 */
export function dropConflictedAvailabilityCells(
  current: MultiCellSelection | null,
  conflictedKeys: Set<string>,
  periods: { id: string; start: string }[],
): MultiCellSelection | null {
  if (!current) return current;
  const cells = current.cells.filter(
    (ref) => !conflictedKeys.has(buildAvailabilityCellConflictKey(ref, periods)),
  );
  return cells.length > 0 ? { ...current, cells } : null;
}

/**
 * True for the three entry statuses that count as "held" for the shared SELECT
 * popover's own/other-reservation lookups (`ownEntry`/`otherEntry`) below and in
 * `offer-availability-mapping.ts`'s `resolveOfferMultiSelectCell` — a live
 * SOFT/HARD reservation or OPTION, never a base/queue/client-projection state.
 * Single source of truth so both resolvers agree on which cells are held.
 */
export function isHeldEntryStatus(status: CellEntry['status']): boolean {
  return status === 'SOFT_RESERVATION' || status === 'HARD_RESERVATION' || status === 'OPTION';
}

/**
 * Resolves a `MultiSelectCellRef` into a `UnifiedSelectCellResolution` for the shared
 * `UnifiedSelectPopover` — including the caller's own releasable reservation/option, when
 * present. Single source of truth shared by the inventory catalog and single-unit detail
 * routes' `resolveCell` callbacks.
 */
export function resolveAvailabilityMultiSelectCell(
  ref: MultiSelectCellRef,
  periods: { id: string; start: string; end: string }[],
  entriesGrid: EntriesGridData,
): UnifiedSelectCellResolution | null {
  const period = periods.find((p) => p.id === ref.periodId);
  if (!period) return null;
  const entries = entriesGrid[ref.unitId]?.[ref.periodId] ?? [];
  const ownEntry = entries.find(
    (e) => e.ownership === 'mine' && e.reservationId !== undefined && isHeldEntryStatus(e.status),
  );
  // Multi-select-eligible (task 4.1) but held by ANOTHER party — the queueable-conflict
  // bucket `resolveSelection` needs to tell a genuinely free cell apart from one the
  // caller may queue for/take outright (task 4.2, `reservation-mixed-range-and-target-expiry`).
  const otherEntry = entries.find((e) => e.ownership === 'other' && isHeldEntryStatus(e.status));
  return {
    mediaUnitId: Number(ref.unitId),
    dateFrom: new Date(`${period.start}T00:00:00.000Z`),
    dateTo: new Date(`${period.end}T00:00:00.000Z`),
    ownReservation:
      ownEntry?.reservationId !== undefined
        ? {
            id: ownEntry.reservationId,
            status: ownEntry.status as 'SOFT_RESERVATION' | 'HARD_RESERVATION' | 'OPTION',
            offerTitle: ownEntry.offerTitle,
            // Already the effective expiry for SOFT/HARD (see UnifiedSelectOwnReservation
            // docstring) — threaded so the SELECT popover can show the same deadline the
            // INSPECT popover would show for a non-own cell (availability-calendar spec).
            ttlExpiresAt: ownEntry.ttlExpiresAt,
            // The hold's own full booking window — threaded so Zwolnij can detect
            // whether the selection under-covers it (CU-86cbh03pj).
            holdDateFrom: ownEntry.dateFrom,
            holdDateTo: ownEntry.dateTo,
          }
        : undefined,
    heldByOther: otherEntry !== undefined,
  };
}

/**
 * Resolves the min(dateFrom)-max(dateTo) span across every `free` (not-own-reservation)
 * cell in an active `MultiCellSelection` — the same window `UnifiedSelectPopover`'s
 * Rezerwacja action would batch-reserve. Used to default the calendar-driven
 * "creating"-mode offer picker's campaign-duration field (CU-86cb8grgr) to the exact
 * month/period the caller just selected. Returns `null` when there is no selection or
 * every selected cell resolves to `null`/an own reservation (nothing free to reserve).
 */
export function resolveFreeCellDateRange(
  selection: MultiCellSelection | null,
  resolveCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null,
): { from: Date; to: Date } | null {
  if (!selection) return null;
  let range: { from: Date; to: Date } | null = null;
  for (const ref of selection.cells) {
    const resolved = resolveCell(ref);
    if (!resolved || resolved.ownReservation) continue;
    range = narrowDateRange(range, { from: resolved.dateFrom, to: resolved.dateTo });
  }
  return range;
}

/**
 * Resolves the distinct media unit ids referenced by an active `MultiCellSelection` —
 * used to carry the selected unit(s) through to the full `/offers/new` form when the
 * caller clicks "Otwórz pełny formularz" from the calendar-driven SELECT popover
 * (CU-86cb8grgn), instead of losing them. Returns `[]` when there is no selection.
 */
export function resolveSelectedMediaUnitIds(selection: MultiCellSelection | null): number[] {
  if (!selection) return [];
  return [...new Set(selection.cells.map((cell) => Number(cell.unitId)))];
}

/**
 * Drops every selected cell whose currently-resolved `ownReservation.id` appears in a
 * `BatchReleaseResponse.skipped[]`-derived reservation-id set — the release-path counterpart
 * to `dropConflictedAvailabilityCells`. Unlike that counterpart, never collapses to `null`
 * when filtering would leave zero cells (see the in-body comment below). Uses the SAME
 * (pre-refetch) `resolveCell` the popover itself uses to look up each cell's reservation id,
 * since a skipped reservation's id is already known synchronously from the batch-release
 * response — no need to wait for the query invalidation's refetch to resolve before deciding
 * which cells stay selected (mirrors how `dropConflictedAvailabilityCells` doesn't wait for a
 * refetch either). Cells with no `ownReservation` (plain free cells the release call never
 * touched) are always kept, matching the "SELECT-eligibility unchanged" rule for the
 * `unified-availability-interaction` spec's "selection SHALL NOT be unconditionally cleared"
 * requirement.
 */
export function dropSkippedAvailabilityCells(
  current: MultiCellSelection | null,
  skippedReservationIds: Set<number>,
  resolveCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null,
): MultiCellSelection | null {
  if (!current) return current;
  const cells = current.cells.filter((ref) => {
    const reservationId = resolveCell(ref)?.ownReservation?.id;
    return reservationId === undefined || !skippedReservationIds.has(reservationId);
  });
  // A lone skipped cell must not collapse the selection to null: the popover only mounts
  // while selection is non-null, and its own local releaseResult state (set in the same
  // onSuccess tick) would be destroyed before it can render the skip explanation. Keep
  // `current` unfiltered in that case.
  return cells.length > 0 ? { ...current, cells } : current;
}

/** Mutation callbacks required by `buildInspectQuickActionHandlers`. */
export interface InspectActionMutations {
  upgradeHard: {
    mutate: (vars: { id: number; expectedStatus: 'SOFT_RESERVATION'; unitId: number }) => void;
  };
  closeSale: {
    mutate: (vars: {
      id: number;
      expectedStatus: 'SOFT_RESERVATION' | 'HARD_RESERVATION';
      unitId: number;
    }) => void;
  };
  cancelReservation: {
    mutate: (
      vars: { id: number; unitId: number; force?: boolean },
      options?: { onError?: (error: unknown) => void },
    ) => void;
  };
  forceRelease: { mutate: (vars: { id: number; unitId: number }) => void };
}

/**
 * Builds the four `InspectPopover` quick-action callbacks from the cell's dominant
 * `CellEntry` — each is `undefined` (button hidden by `InspectPopover` itself) when the
 * dominant entry has no `reservationId` or an ineligible status. Single source of truth
 * shared by the inventory catalog and single-unit detail routes.
 */
export function buildInspectQuickActionHandlers(
  dominant: CellEntry | undefined,
  unitId: number,
  mutations: InspectActionMutations,
): {
  onUpgradeHard?: () => void;
  onCloseSale?: () => void;
  onCancel?: () => void;
  onForceRelease?: () => void;
} {
  const reservationId = dominant?.reservationId;
  if (dominant === undefined || reservationId == null) {
    return {
      onUpgradeHard: undefined,
      onCloseSale: undefined,
      onCancel: undefined,
      onForceRelease: undefined,
    };
  }
  const isActiveHold =
    dominant.status === 'SOFT_RESERVATION' || dominant.status === 'HARD_RESERVATION';
  return {
    onUpgradeHard:
      dominant.status === 'SOFT_RESERVATION'
        ? () =>
            mutations.upgradeHard.mutate({
              id: reservationId,
              expectedStatus: 'SOFT_RESERVATION',
              unitId,
            })
        : undefined,
    onCloseSale: isActiveHold
      ? () =>
          mutations.closeSale.mutate({
            id: reservationId,
            expectedStatus: dominant.status as 'SOFT_RESERVATION' | 'HARD_RESERVATION',
            unitId,
          })
      : undefined,
    // `showAnuluj` (resolveQuickActionFlags) only renders this on a non-owned hold when
    // the caller also holds reservation.force_release — mirrors reservation-list-view's
    // `dispatch('cancel', { force: !isOwn })` precedent so the already-visible button
    // actually succeeds instead of 403ing via assertHolderOrForce.
    onCancel: () =>
      mutations.cancelReservation.mutate(
        { id: reservationId, unitId, force: dominant.ownership !== 'mine' },
        // useCancelReservation deliberately has no hook-level onError (would double
        // alongside useReservationActions.dispatch's own call-level onError for the
        // SAME mutation — TanStack Query fires both) — this call site has no other
        // onError of its own, so it supplies one here to avoid the original silent-403
        // swallow (CU-86cbh03pj QA fix).
        {
          onError: (error) => toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
        },
      ),
    onForceRelease: () => mutations.forceRelease.mutate({ id: reservationId, unitId }),
  };
}
